// TopBar — 48 px header per UI_SPEC §3.1.
//
// Left:  brand mark + project breadcrumb (project pill opens a real
//        list_projects dropdown) + current view name.
// Right: Rebuild · Diff · avatar. Each action button opens the chat
//        slide-over with a pre-filled tool-call prompt.

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useShell } from "./ShellContext.js";
import { useTool } from "../lib/useTool.js";

const VIEW_TITLE: Record<string, string> = {
  "/":                    "Dashboard",
  "/gantt":               "Schedule / Gantt",
  "/wbs":                 "Work-breakdown",
  "/critical-path":       "Critical path",
  "/dependency-network":  "Dependency network",
  "/risk":                "Risk register",
  "/earned-value":        "Earned value",
  "/resources":           "Resources",
  "/work-packages":       "Work-packages",
  "/canonical":           "Canonical YAML",
  "/ops":                 "Health & ops",
};

interface ProjectRow {
  id?: string;
  name?: string;
  active?: boolean;
}

export function TopBar() {
  const { project, setProject, openChat } = useShell();
  const { pathname } = useLocation();
  const title = VIEW_TITLE[pathname] ?? "Dashboard";

  return (
    <header className="flex h-12 flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4">
      <div className="flex items-center gap-3">
        <Link to="/" className="flex items-center gap-3 hover:opacity-80">
          <div className="h-6 w-6 rounded bg-slate-900" />
          <span className="font-semibold">pm-tools</span>
        </Link>
        <span className="text-slate-300">/</span>
        <ProjectPicker project={project} onPick={setProject} />
        <span className="text-slate-300">/</span>
        <span className="text-sm text-slate-600">{title}</span>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <button
          type="button"
          className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-100"
          onClick={() => openChat("Rebuild the project: run build_all and re-import all three apps.")}
        >Rebuild</button>
        <button
          type="button"
          className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-100"
          onClick={() => openChat("Show the recent diff on project.yaml.")}
        >Diff</button>
        <div className="ml-1 h-6 w-6 rounded-full bg-slate-200" title="single-user session" />
      </div>
    </header>
  );
}

function ProjectPicker({ project, onPick }: {
  project: string;
  onPick: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Fetch list_projects only when the dropdown is opened — saves a tool
  // call on every TopBar mount, and refreshes if the user re-opens later.
  const { env, loading, error, refetch } = useTool("list_projects", open ? {} : null);

  // Close on outside click and on Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const rows = ((env?.data as { rows?: ProjectRow[] } | undefined)?.rows ?? []) as ProjectRow[];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-sm hover:bg-slate-100"
        onClick={() => setOpen((v) => !v)}
        title="Switch project"
      >
        {project} ▾
      </button>
      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500">
            Switch project
          </div>
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500">Loading…</div>
          )}
          {error && (
            <div className="px-3 py-2 text-xs text-rose-600">
              {error}
              <button type="button" onClick={refetch}
                      className="ml-2 text-sky-700 hover:underline">retry</button>
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">No projects found.</div>
          )}
          {!loading && !error && rows.map((p) => {
            const id = String(p.id ?? p.name ?? "");
            const active = id === project;
            return (
              <button
                key={id}
                type="button"
                onClick={() => { onPick(id); setOpen(false); }}
                className={
                  "block w-full px-3 py-1.5 text-left text-xs transition-colors " +
                  (active
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100")
                }
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono">{id}</span>
                  {active && <span className="text-[10px]">(active)</span>}
                </div>
                {p.name && p.name !== id && (
                  <div className={"text-[10px] " + (active ? "text-slate-300" : "text-slate-500")}>
                    {p.name}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
